/* eslint-disable no-unused-vars */
import Chart from 'react-apexcharts';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  Typography
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import ArrowRightIcon from '../../../icons/ArrowRight';
import { useCallback, useState, useEffect } from 'react';
import useMounted from '../../../hooks/useMounted';
import moment from 'moment';
import useAuth from '../../../hooks/useAuth';
import { relaunchApi } from '../../../api/relaunchApi';
import PropTypes from 'prop-types';

const RelaunchStats = (props) => {
  const { state } = props;
  const theme = useTheme();
  const mounted = useMounted();
  const { user } = useAuth();
  const [infoRelaunch, setInfoRelaunch] = useState([]);
  
  const correspondence = { 
    shift: 'Décalage', 
    turn1: 'Relance Tour 1', 
    turn2: 'Relance Tour 2', 
    fc: 'Formation continue' 
  };

  const stateThemes = {
    shift: {
      color: '#f59e0b',
      bgLight: 'rgba(245, 158, 11, 0.08)',
      bgDark: 'rgba(245, 158, 11, 0.15)',
      gradient: 'linear-gradient(90deg, #f59e0b 0%, #d97706 100%)'
    },
    turn1: {
      color: '#3b82f6',
      bgLight: 'rgba(59, 130, 246, 0.08)',
      bgDark: 'rgba(59, 130, 246, 0.15)',
      gradient: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)'
    },
    turn2: {
      color: '#8b5cf6',
      bgLight: 'rgba(139, 92, 246, 0.08)',
      bgDark: 'rgba(139, 92, 246, 0.15)',
      gradient: 'linear-gradient(90deg, #8b5cf6 0%, #7c3aed 100%)'
    },
    fc: {
      color: '#10b981',
      bgLight: 'rgba(16, 185, 129, 0.08)',
      bgDark: 'rgba(16, 185, 129, 0.15)',
      gradient: 'linear-gradient(90deg, #10b981 0%, #059669 100%)'
    }
  };

  const getRelaunch = useCallback(async () => {
    try {
      const data = await relaunchApi.getRelaunch(state, localStorage.getItem('accessToken'));
      if (data.length === 0) {
        throw new Error('Aucune relance possible');
      }

      if (mounted.current) {
        setInfoRelaunch(data);
      }
    } catch (err) {
      console.error(err);
    }
  }, [mounted, state]);

  useEffect(() => {
    getRelaunch();
  }, [getRelaunch]);

  return (
    <Box sx={{ py: 1 }}>
      <Grid container spacing={3}>
        {infoRelaunch.map((info) => {
          const activeTheme = stateThemes[info.state] || {
            color: theme.palette.primary.main,
            bgLight: 'rgba(59, 130, 246, 0.08)',
            bgDark: 'rgba(59, 130, 246, 0.15)',
            gradient: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)'
          };

          const chartOptions = {
            chart: {
              background: 'transparent',
              stacked: false,
              toolbar: {
                show: false
              }
            },
            colors: [activeTheme.color],
            labels: ['Progression'],
            plotOptions: {
              radialBar: {
                dataLabels: {
                  name: {
                    show: true,
                    fontSize: '10px',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    offsetY: -8
                  },
                  value: {
                    show: true,
                    fontSize: '18px',
                    fontWeight: 800,
                    color: theme.palette.text.primary,
                    offsetY: 6,
                    formatter: (val) => `${val}%`
                  }
                },
                hollow: {
                  size: '62%'
                },
                track: {
                  background: theme.palette.mode === 'light' ? '#f1f5f9' : 'rgba(255, 255, 255, 0.05)'
                }
              }
            },
            theme: {
              mode: theme.palette.mode
            }
          };
          
          const chartSeries = [info.progression === null ? 0 : info.progression];
          
          let isUrgent = false;
          const limitDate = new Date(info.end_date);
          if (new Date() >= limitDate.setDate(limitDate.getDate() - 20)) {
            isUrgent = true;
          }

          return (
            ((info.nbCustomers !== '0' && user.id_service === 4) || user.access_level === 0) && (
              <Grid
                item
                xs={12}
                md={6}
                key={`${info.state}-${info.end_date}`}
              >
                <Card
                  sx={{
                    borderRadius: '24px',
                    border: '1px solid',
                    borderColor: isUrgent 
                      ? 'rgba(239, 68, 68, 0.4)' 
                      : (theme.palette.mode === 'light' ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.05)'),
                    background: theme.palette.mode === 'light' 
                      ? 'rgba(255, 255, 255, 0.65)' 
                      : 'rgba(15, 23, 42, 0.45)',
                    backdropFilter: 'blur(20px)',
                    boxShadow: theme.palette.mode === 'light'
                      ? '0 8px 32px rgba(148, 163, 184, 0.05)'
                      : '0 8px 32px rgba(0, 0, 0, 0.15)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '4px',
                      background: isUrgent ? 'linear-gradient(90deg, #ef4444, #f97316)' : activeTheme.gradient,
                      opacity: 0.85
                    },
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      boxShadow: isUrgent 
                        ? '0 20px 40px rgba(239, 68, 68, 0.12)' 
                        : '0 20px 40px rgba(59, 130, 246, 0.08)',
                      borderColor: isUrgent ? 'rgba(239, 68, 68, 0.6)' : activeTheme.color,
                    }
                  }}
                >
                  <CardContent
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 3,
                      p: 3.5,
                      flexGrow: 1
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <Chart
                        height="160"
                        options={chartOptions}
                        series={chartSeries}
                        type="radialBar"
                        width="160"
                      />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, mt: -1.5 }}>
                        Semaine en cours
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flexGrow: 1,
                        textAlign: { xs: 'center', sm: 'left' }
                      }}
                    >
                      <Typography
                        color="text.primary"
                        variant="h6"
                        sx={{ fontWeight: 800, mb: 1, lineHeight: 1.3, letterSpacing: '-0.01em' }}
                      >
                        {`Sessions terminant le ${moment(info.end_date).format('DD/MM/YYYY')}`}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'center', sm: 'flex-start' }, alignItems: 'center', mt: 0.5 }}>
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            backgroundColor: theme.palette.mode === 'light' ? activeTheme.bgLight : activeTheme.bgDark,
                            color: activeTheme.color
                          }}
                        >
                          {correspondence[info.state]}
                        </Box>
                        
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 0.5,
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            backgroundColor: theme.palette.mode === 'light' ? 'rgba(15, 23, 42, 0.04)' : 'rgba(255, 255, 255, 0.04)',
                            color: 'text.secondary',
                            border: '1px solid',
                            borderColor: theme.palette.mode === 'light' ? 'rgba(226, 232, 240, 0.8)' : 'rgba(255, 255, 255, 0.05)',
                          }}
                        >
                          {`${info.nbCustomers} client${parseInt(info.nbCustomers, 10) > 1 ? 's' : ''}`}
                        </Box>

                        {isUrgent && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              backgroundColor: theme.palette.mode === 'light' ? 'rgba(239, 68, 68, 0.08)' : 'rgba(239, 68, 68, 0.15)',
                              color: 'error.main',
                              border: '1px solid',
                              borderColor: 'transparent'
                            }}
                          >
                            Urgent
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                  <CardActions
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      px: 3, 
                      py: 2,
                      bgcolor: theme.palette.mode === 'light' ? 'rgba(248, 250, 252, 0.4)' : 'rgba(255, 255, 255, 0.01)',
                      borderTop: '1px solid',
                      borderColor: theme.palette.mode === 'light' ? 'rgba(226, 232, 240, 0.5)' : 'rgba(255, 255, 255, 0.04)'
                    }}
                  >
                    <Button
                      component={RouterLink}
                      to={`/dashboard/relaunch/${info.state}/${info.end_date}`}
                      color="primary"
                      endIcon={
                        <ArrowRightIcon 
                          fontSize="small" 
                          sx={{ 
                            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' 
                          }} 
                        />
                      }
                      variant="text"
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: 'primary.dark',
                          '& .MuiSvgIcon-root': {
                            transform: 'translateX(4px)'
                          }
                        }
                      }}
                    >
                      Plus d&apos;informations
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            )
          );
        })}
      </Grid>
    </Box>
  );
};

RelaunchStats.propTypes = {
  state: PropTypes.string.isRequired
};

export default RelaunchStats;
